// crm-app/cdnVendor.js
//
// Centralized registry of third-party CDN scripts loaded dynamically by
// the CRM SPA. Each entry pins an exact version + SHA-384 integrity hash
// so a CDN compromise cannot inject JavaScript into our authenticated
// origin (audit F-N4, 2026-05-22).
//
// To bump a library:
//   1. Update its `url` here to the new version.
//   2. Recompute the hash:
//        curl -sL <URL> | openssl dgst -sha384 -binary | base64 -w0
//   3. Update the `integrity` string with `sha384-<output>`.
//   4. Test the affected feature (Import xlsx/pdf/docx, Licenses OCR,
//      Commission Tracker, Reports xlsx export).
//
// Why dynamic loading and not a bundle: these libraries are 1-15 MB
// each (xlsx, pdf.js, tesseract). Bundling them statically into the
// SPA inflates first-load by ~10x for users who never use the import
// or OCR flow. Lazy-loading on demand keeps the cold-start fast.
//
// Loaders for dynamic ESM `import('https://cdn.sheetjs.com/...')` calls
// cannot carry an integrity attribute (ESM imports do not support SRI
// without an import map). Those four call sites remain unprotected by
// SRI; mitigation depends on the SheetJS CDN's own integrity (TLS +
// vendor reputation). A future task can migrate xlsx to a bundled
// import via `npm install xlsx` if the SPA bundle budget allows.

export const PDFJS_3_11_174 = Object.freeze({
  url: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
  integrity: 'sha384-/1qUCSGwTur9vjf/z9lmu/eCUYbpOTgSjmpbMQZ1/CtX2v/WcAIKqRv+U1DUCG6e',
});
export const PDFJS_WORKER_3_11_174 = Object.freeze({
  url: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
  integrity: 'sha384-SnzOobpRMLXZ52iJvZm/C0fYw0OQemTXzTjIsdsfMcrCtCEe9qgzxTd3RSklO5x2',
});
export const PDFJS_4_4_168 = Object.freeze({
  url: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs',
  integrity: 'sha384-fzqD3KLclV6zyTSuToNuSG370CipiqN572vfT2zti7ORKnQPaJrvsz8deY+/Tgwe',
});
export const XLSX_FULL_0_18_5 = Object.freeze({
  url: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js',
  integrity: 'sha384-vtjasyidUo0kW94K5MXDXntzOJpQgBKXmE7e2Ga4LG0skTTLeBi97eFAXsqewJjw',
});
export const MAMMOTH_1_8_0 = Object.freeze({
  url: 'https://cdn.jsdelivr.net/npm/mammoth@1.8.0/mammoth.browser.min.js',
  integrity: 'sha384-/cXAMbzovUIKbBERjPmR3SnPTh8siWr5lsvFYj1Uq4XP0yaJUZJmsh0YXyGv5P0y',
});
export const TESSERACT_5_1_1 = Object.freeze({
  url: 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js',
  integrity: 'sha384-GJqSu7vueQ9qN0E9yLPb3Wtpd7OrgK8KmYzC8T1IysG1bcvxvIO4qtYR/D3A991F',
});

// loadCdnScript(meta): inject a <script src+integrity+crossorigin> tag
// and resolve when it loads. Idempotent — if a script with the same
// URL already lives in the document, resolves immediately.
export function loadCdnScript(meta) {
  return new Promise((resolve, reject) => {
    if (!meta || !meta.url || !meta.integrity) {
      reject(new Error('loadCdnScript: missing url or integrity'));
      return;
    }
    // Idempotency: an existing matching <script src> means the browser
    // already verified the integrity hash on the first load (or rejected
    // it before this attempt).
    if ([...document.scripts].some((s) => s.src === meta.url)) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = meta.url;
    el.integrity = meta.integrity;
    el.crossOrigin = 'anonymous'; // SRI requires CORS-enabled fetch
    el.async = false; // preserve dependency order between paired scripts
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('script-load-or-integrity-failed'));
    document.head.appendChild(el);
  });
}

// pdf.js listo para usar (script + worker), una sola vez. Lo usan el editor de
// sitios de firma y la página del cliente que firma sobre el PDF
// (components/SitiosDeFirmaEnPdf.jsx). LicenseForm.jsx (`ensurePdfjs`) y el
// lector de plantillas de SignaturesView.jsx cargan pdf.js con su propia copia
// de estas dos líneas desde antes; se pueden pasar a esta cuando se toquen.
export async function cargarPdfJs() {
  if (!window.pdfjsLib) await loadCdnScript(PDFJS_3_11_174);
  if (window.pdfjsLib && !window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_3_11_174.url;
  }
  if (!window.pdfjsLib) throw new Error('pdfjs-load-failed');
  return window.pdfjsLib;
}
